import { ShieldCheck } from "lucide-react";
import { useSeo } from "../lib/seo";
import { LegalPage } from "../components/legal/LegalPage";
import type { LegalSection } from "../components/legal/LegalPage";

const SECTIONS: LegalSection[] = [
  {
    id: "who-we-are",
    title: "Who we are",
    body: (
      <>
        <p>
          Wise 2.0 (the "Platform") is an AI-powered integration platform operated by{" "}
          <strong>Shineywise Technologies OPC Pvt.&nbsp;Ltd.</strong> and{" "}
          <strong>Shineywise Technologies LLC</strong> (together, "Shineywise", "we", "us").
          For workspace data, your organization is the data controller and Shineywise acts as a
          processor; for account and billing data we act as a controller.
        </p>
      </>
    ),
  },
  {
    id: "scope",
    title: "Scope of this policy",
    body: (
      <p>
        This policy covers the Platform (web application, APIs, connectors, workflows, the
        Connector Exchange, and monitoring services) and our marketing site. It does not cover
        the third-party systems you connect — Salesforce, SAP, NetSuite and others remain governed
        by their own terms and privacy policies.
      </p>
    ),
  },
  {
    id: "data-we-collect",
    title: "Information we collect",
    body: (
      <>
        <p>We process the following categories of information:</p>
        <ul className="space-y-1.5">
          <li>
            <strong>Account data</strong> — name, work email, hashed password (Argon2id; we can
            never read your password), role, and workspace memberships.
          </li>
          <li>
            <strong>Workspace configuration</strong> — connections, parameter schemas, workflows,
            and transformation mappings you create. Configuration is stored as cryptographically
            signed XML.
          </li>
          <li>
            <strong>Connected-system credentials</strong> — API keys, tokens, and passwords you
            provide for your systems are written to a dedicated secrets vault (HashiCorp Vault)
            and referenced by path only. They never appear in configuration files, logs, AI
            prompts, or the Connector Exchange.
          </li>
          <li>
            <strong>Integration content</strong> — records that flow through your connectors and
            workflows are processed transiently to execute the operations you request and are not
            retained beyond operational logs and the outputs you store.
          </li>
          <li>
            <strong>Usage & telemetry</strong> — audit events, connection health checks, error
            diagnostics, and product analytics needed to operate and secure the service.
          </li>
          <li>
            <strong>Support communications</strong> — messages you send to{" "}
            <a className="font-medium text-brand-700 hover:underline" href="mailto:info@shineywise.com">
              info@shineywise.com
            </a>
            .
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "how-we-use",
    title: "How we use information",
    body: (
      <>
        <p>We use information to:</p>
        <ul className="space-y-1.5">
          <li>provide, operate, and maintain the Platform and your integrations;</li>
          <li>generate, sandbox-test, sign, and register the connectors you request;</li>
          <li>run scheduled workflows and federated queries you configure;</li>
          <li>monitor connection health and heal configuration drift (the monitoring agent);</li>
          <li>secure the service — fraud, abuse, and incident detection and response;</li>
          <li>communicate service updates, invites, and support responses;</li>
          <li>improve reliability and usability using aggregate, de-identified usage patterns.</li>
        </ul>
        <p>
          <strong>We do not sell personal data</strong> and we do not use your workspace content
          for advertising.
        </p>
      </>
    ),
  },
  {
    id: "ai-processing",
    title: "AI processing",
    body: (
      <>
        <p>
          When you create or extend a connector from a prompt, the natural-language description
          (and, for extensions, the connector's own source code) is sent to our large-language-model
          subprocessor to draft code and configuration schemas. Prompts are limited to what you
          type plus platform instructions — <strong>your stored credentials are never included</strong>.
        </p>
        <p>
          AI-generated code is treated as untrusted: it executes only inside an isolated gVisor
          sandbox on a dedicated host, with no network access during testing, and is registered
          only after its test suite passes. Our LLM subprocessor is contractually restricted from
          using API inputs to train models.
        </p>
      </>
    ),
  },
  {
    id: "sharing",
    title: "Sharing & subprocessors",
    body: (
      <>
        <p>We share data only with subprocessors necessary to run the service:</p>
        <ul className="space-y-1.5">
          <li>cloud infrastructure and object storage providers (hosting, backups);</li>
          <li>the LLM provider used for connector generation;</li>
          <li>transactional email delivery (signup, invites, password reset).</li>
        </ul>
        <p>
          Each subprocessor is bound by a data-processing agreement. We may also disclose
          information when required by law, after verifying the request's validity and, where
          lawful, notifying you.
        </p>
        <p>
          Publishing to the <strong>Connector Exchange</strong> shares a connector's code bundle,
          capability list, and parameter <em>schema</em> with other workspaces — configured values
          and secrets are never included.
        </p>
      </>
    ),
  },
  {
    id: "security",
    title: "Security measures",
    body: (
      <>
        <ul className="space-y-1.5">
          <li><strong>Tenant isolation</strong> — row-level security in the database; every query is workspace-scoped.</li>
          <li><strong>Secrets management</strong> — credentials live in HashiCorp Vault; configs carry references only.</li>
          <li><strong>Sandboxed execution</strong> — AI-generated code runs exclusively in a gVisor micro-VM sandbox on a segregated host, reached over mutually-authenticated TLS.</li>
          <li><strong>Signed configuration</strong> — every config version is Ed25519-signed; tampered payloads fail verification.</li>
          <li><strong>Authentication</strong> — Argon2id password hashing, short-lived access tokens, role-based access control.</li>
          <li><strong>Encryption in transit</strong> — TLS on all external interfaces.</li>
          <li><strong>Auditability</strong> — administrative and data-changing actions are written to an append-only audit log.</li>
        </ul>
      </>
    ),
  },
  {
    id: "retention",
    title: "Data retention & deletion",
    body: (
      <>
        <p>
          Workspace data is retained while your workspace is active. Deleting a connection removes
          its configuration objects and code bundle; its Vault secrets are deleted on a short
          schedule. When a workspace is closed we delete or de-identify its data within 30 days,
          except where law requires longer retention (e.g., billing records) or backups persist
          for up to 35 days on a rolling basis.
        </p>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "Your rights",
    body: (
      <>
        <p>
          Depending on your jurisdiction (including the GDPR and CCPA/CPRA), you may have the
          right to access, correct, export, restrict, object to the processing of, or delete your
          personal data, and the right not to be discriminated against for exercising those
          rights. Workspace members should direct requests to their workspace owner; you can also
          contact us directly at{" "}
          <a className="font-medium text-brand-700 hover:underline" href="mailto:info@shineywise.com">
            info@shineywise.com
          </a>
          . We respond within 30 days.
        </p>
      </>
    ),
  },
  {
    id: "transfers",
    title: "International transfers",
    body: (
      <p>
        Where personal data is transferred across borders (including between our Indian and US
        entities and our subprocessors), we rely on appropriate safeguards such as standard
        contractual clauses and equivalent mechanisms recognized in your jurisdiction.
      </p>
    ),
  },
  {
    id: "breach",
    title: "Incident notification",
    body: (
      <p>
        If we become aware of a breach of security affecting your personal data, we will notify
        affected customers without undue delay — and in any case within the timelines required by
        applicable law — including the nature of the incident, the data involved, and the
        remediation steps taken.
      </p>
    ),
  },
  {
    id: "children",
    title: "Children",
    body: (
      <p>
        The Platform is a business tool and is not directed at children under 16. We do not
        knowingly collect personal data from children.
      </p>
    ),
  },
  {
    id: "changes",
    title: "Changes to this policy",
    body: (
      <p>
        We may update this policy as the Platform evolves. Material changes are announced in-app
        and by email to workspace owners at least 14 days before they take effect. The "Last
        updated" date above always reflects the current version.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <p>
        Shineywise Technologies OPC Pvt.&nbsp;Ltd. · Shineywise Technologies LLC —{" "}
        <a className="font-medium text-brand-700 hover:underline" href="mailto:info@shineywise.com">
          info@shineywise.com
        </a>{" "}
        ·{" "}
        <a
          className="font-medium text-brand-700 hover:underline"
          href="https://shineywise.com"
          target="_blank"
          rel="noreferrer"
        >
          shineywise.com
        </a>
      </p>
    ),
  },
];

export default function Privacy() {
  useSeo({
    title: 'Privacy Policy',
    description: 'How Shineywise Technologies collects, uses, and protects your data across the Wise 2.0 platform.',
    path: '/privacy',
  });
  return (
    <LegalPage
      icon={ShieldCheck}
      eyebrow="Legal"
      title="Privacy Policy"
      updated="July 2026"
      intro={
        <p>
          Wise 2.0 was designed so that your credentials and business data stay under your
          control: secrets live in a vault, AI-generated code runs in an isolated sandbox, every
          configuration is cryptographically signed, and every workspace is isolated. This policy
          explains, in plain language, what we process and why.
        </p>
      }
      sections={SECTIONS}
    />
  );
}
